import { not, or, setup } from 'xstate';

import { routePath } from 'config/route-config';
import { actionUrl } from 'config/static-content';
import { events } from 'machines/config/events';
import { guards } from 'machines/config/guards';
import { stringToBool } from 'utils/misc';

import type { TContext, TEvent } from 'machines/types/machine-config';

export const moveInMachineConfig = {
  id: 'moveInMachine',
  context: {
    guards: {}
  },
  initial: 'home',
  states: {
    home: {
      on: {
        NEXT: {
          target: 'findAddress'
        },
        BACK: {
          target: 'externalBgHomeMove'
        },
        BLOCK_JOURNEY: {
          target: 'businessUpdatesAnnouncement'
        }
      },

      meta: {
        path: routePath.moveIn
      }
    },

    businessUpdatesAnnouncement: {
      on: {
        ALLOW_JOURNEY: {
          target: 'home'
        }
      },

      meta: {
        path: routePath.businessUpdatesAnnouncement
      }
    },

    findAddress: {
      on: {
        NEXT: [
          {
            target: 'exceptions',
            description: "not('isMoveInEligible')",
            guard: not('isMoveInEligible')
          },
          {
            target: 'splitSupply',
            guard: 'isNepSplitSupply'
          },
          {
            target: 'classicSupply',
            description: "or(['isBGCustomer', 'isSapSplitSupply'])",
            guard: or(['isBGCustomer', 'isSapSplitSupply'])
          },
          {
            target: 'nonSupply',
            guard: 'isNewToBrand'
          },
          {
            target: 'moveInDate'
          }
        ],
        LOGIN: {
          target: 'externalLogin'
        }
      },
      meta: {
        path: routePath.findAddress
      }
    },
    splitSupply: {
      on: {
        NEXT: {
          target: 'moveInDate'
        }
      },
      meta: {
        path: routePath.splitSupply
      }
    },
    classicSupply: {
      on: {
        NEXT: {
          target: 'externalBgClassicHomeMove'
        }
      },
      meta: {
        path: routePath.classicSupply
      }
    },
    nonSupply: {
      on: {
        NEXT: {
          target: 'externalDirectSales'
        }
      },
      meta: {
        path: routePath.nonSupply
      }
    },
    moveInDate: {
      on: {
        NEXT: [
          {
            target: 'exceptions',
            description: "not('isMoveInEligible')",
            guard: not('isMoveInEligible')
          },
          {
            target: 'newHome'
          }
        ]
      },
      meta: {
        path: routePath.moveDate
      }
    },
    exceptions: {
      on: {
        CHAT: {
          target: 'externalContactUs'
        }
      },
      meta: {
        path: routePath.exceptions
      }
    },

    newHome: {
      on: {
        NEXT: {
          target: 'consumptionEntry'
        }
      },
      meta: {
        path: routePath.newHome
      }
    },

    consumptionEntry: {
      on: {
        NEXT: [
          {
            target: 'quote'
          }
        ]
      },
      meta: {
        path: routePath.consumptionEntry
      }
    },

    quote: {
      on: {
        NEXT: {
          target: 'createAccount'
        }
      },
      meta: {
        path: routePath.quote
      }
    },

    createAccount: {
      on: {
        NEXT: [
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'meterReads'
          }
        ],
        CHANGE_ADDRESS: { target: 'findAddress' }
      },
      meta: {
        path: routePath.createAccount
      }
    },

    directDebit: {
      on: {
        NEXT: {
          target: 'meterReads'
        }
      },
      meta: {
        path: routePath.directDebit
      }
    },

    meterReads: {
      on: {
        NEXT: {
          target: 'psrEntry'
        },
        CHAT: {
          target: 'externalContactUs'
        }
      },
      meta: {
        path: routePath.meterReads
      }
    },

    psrEntry: {
      on: {
        NEXT: [
          {
            target: 'userPreferences'
          }
        ]
      },
      meta: {
        path: routePath.psrEntry
      }
    },

    userPreferences: {
      on: {
        NEXT: {
          target: 'orderSummary'
        }
      },
      meta: {
        path: routePath.userPreferences
      }
    },

    orderSummary: {
      on: {
        NEXT: {
          target: 'orderComplete'
        }
      },
      meta: {
        path: routePath.orderSummary
      }
    },
    orderComplete: {
      on: {
        SECOND_QUOTE: {
          target: 'externalSecondQuote'
        }
      },
      meta: {
        path: routePath.orderComplete
      }
    },
    externalLogin: {
      meta: {
        path: actionUrl.login
      }
    },
    externalContactUs: {
      meta: { path: actionUrl.contact }
    },
    externalBgClassicHomeMove: {
      meta: { path: actionUrl.bgClassicHomeMoveUrl }
    },
    externalDirectSales: {
      meta: { path: actionUrl.directSales }
    },
    externalSecondQuote: {
      meta: { path: actionUrl.secondQuote }
    },
    externalBgHomeMove: {
      meta: { path: actionUrl.bgHomeMove }
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ...(events as any)
};

const { VITE_XSTATE_DEBUG } = import.meta.env;
const debug = stringToBool(VITE_XSTATE_DEBUG);
// for VS extesnion
/* istanbul ignore next -- @preserve */
debug &&
  setup({
    types: {} as {
      context: TContext;
      events: TEvent;
    },
    guards
  }).createMachine({
    /** @xstate-layout N4IgpgJg5mDOIC5QFsD2A3MBJAdgWQEMBjACwEscwA6E1ZMAYgDkBRADQBUBtABgF1EoAA6pYZAC5lUOQSAAeiAIwBWRVQBsAFgAc6gEx7tigOwBORab0AaEAE9EmnsqrLlxvaYDMPHosXbTdU8AX2CbNExcQlIKalp6BgAhAEEAYQBpXgEkEBExSWlZBQQVNS1dAyMzC2s7RE9NYyoeY0dFIM9lTRVPY1DwjGx8YnJKGjpGRIAZAHkMgH0AKRmAVQAlVgBNLNk8iSkZHOLSjR19QxNzSxt7BG09TRdTKs9vD3UtfpAIoejR6gARgBXMSUWCwFZCCAEcRwZI4HCoIE4Ihgeg4cTMdjcfi7UT7QpHRDqZTaKieSzKII8bSqVy1W7Ge4uZR6HimMzqbROTyKL4-KIjWJUYGguAQqEwuEIpEotFgDEMZJTWYAdSWqw2LG2uJyewKh1Ax1UpwqF2q1zqCGMrSojRUdOUFO0mhCYW+g0FMTGoti4Mh0NhsAAykCAcgJCxkAQyAAbLGcHZ6-EGopKAymJ6KBrKLwecw3RBMvQstkcwLcp1890C4be6gAMwoEGSEAgACdxQmcdlhCmDmmECSyRS9FTvLSHQzEKyS2z1O5uoY9Ez1PzPXX-lQmzgW23O+Du1xFL3cv3CUbiaTyZTqZP6YWEIYmpovM89Io9B9nmuaxu-sKO57h2XasImeinvqA5EkO16juONKOtOT5OFQ2jGK8fjdJoY7aLo66RJugHNq2IGHmB3CeJB56GvIV4jreE5IY+6g8J4aErhSOiOMY-hugMhEAWMQGkQesBHpo1H5NBl6wQxY53sxVqOGSTjuF4Ph+AEQQEb8QrCSR+5drMADiWBMEmfbSRedFyTeClMVOj6Ls0XKWAu2ieMuv4CXp9ZULAQixhIoZCEFthHrqVkErRxwZlmOZ5qYBZWs+dpvvcn7fvhf6Cfp1BELGBDgmQRCheFkVSTFg6fh4CWaLmXnJYoj4GGUbKvKSpi5uhC66V6W6Ijg5WxhFFGWWe1mxYgbIqGh5b+IoLR6L0LFsRxGGmNxLR8f1RFjLWAAiUqVXiU01R8zjtJorENCuhieY+ni0vNQR4TabKkn0uV+VuR0neNJ5ndVMHtCSVDXbdOHuHhnitXh5KcSoN0+Lyph7UJ1BgHIqJCAa4mpAAEskPbA6mMGzc4ATVEYy2rcpNLzTw+gdA1GbKBj+VUJQADuhMTKdybnRTvhUwttPuPTtwPYjGHI6xbEWJz-lENIsBAsgeMHCwGLtmN2ITVBNnFCtjM8B4Ki6BhHxsfDI5I10Cto8rW4AI5AqgsKC9F5OyWOLRUB+6FfSSxg0nbsu8o7qNKz9A3CkQnZSskRCq8imLjVFk0g376HsUyN3JRSW2+MYj66FQHIfAu86upojQuwnSewinaeKoDVW+7Zz75y66hF8lbRl6lAdOslTI0uyY6so3YyJ2Ayep7KmJE8kTAmSw8zJIdh1rCwwbBobNGDv7TRB8YIduOHVovbyPg+GPxgedWvnx2MEBkJ2RDiIdYAAhI3ts5d2KF5LQ817iNXNs+TQ5d1CVyfh8dwzM64Nzjvtag9BYTtjWAvCA4lM6dxkrZTo7INBLUUK6VkJhczlyaLTe+j9n6zwwWALBOCCB4IYKvUmQsc7EOUKQ9Q5DKEfgvqYR82ZMw8AaoPbobEBEvw9HlfyQhYDth1uIPWgCjbTQQM9aRLh3BGPNktc2w9bjoTQqYh+FIn5MkUbWTGVAQRgHbAABU7A2VxCpUT4INlnHRg4VJlB0BfF0VIn6floVY++PIq72OYVQVA7YICuNDMgaMWiCFkyIcUOucDkoeB6jhKo0T6E2PiUYRJyTUntlSHQIKrDGCHSwHvVIHB5jBmVPvI+wtZI3WzOlMcDQFzjm0BIryGg0buDZB4Xi2ZqkpNcfUzWsYmkMGDCwVIMwmCHXmAARRWDMDgLBel8LyUI9ir5hk3Qvm9cuagKSsVpEEDqS03TukRKk+AORHH5RycbRAABadQj4QXNFiZC2J31X7oPGPQAFuicKPm6GoIxrwhEeAMDaRJvowQSkDNKREyJUTonEIiwcIyqAYQUdIz8Iz0LOWZHnJ+gRUa9G0LikEfoCVShDGGCM4gowxljBSmC3UyRaAarmCw9IORMpLCy-uTsOVcrFP6SUQZQzhkjNGOMoZU7ijFbJbMkyuQyJXKYHwjRkLFjQhhVlKqmSJJEkZcExrbI2upWHHCbFrm9RRahdCVreJeF5O0DmaCnGBWCuIEatwfa5KUObdiyUSROirCtFa8M6EqBJL0LqnlXiJMKsVMQZUgRhVGh644vhHj1waN1NmXInStQ-JXX1LRWT3EXD5JRv1hRDXjTWpQeESzSLcIECh-cPxrUeBQq1HxGjeDYok-6sIR0lBwnAtiDxWjPBum8Vq3VXKGA+J5Dk-gcVRq5tjXG+NN3+GZBOzk07LAtQZpmD8GZORchaDC-tb9qC835gi3hwDR3MsXfOHaH0I7lGeGHSwvI+o3pVmrDWWtpAaL1pu79PBA6sVus9LQS0YGpQRloK1W0KSyqtok92nswB4dRRDAwjhXDeF4h8eDr52SuiuKSVDsKnHz0Xm3cl4Gk1Pl4iWVQ-6uiox0I+Lw1Lar+CE5+bMkaRNcw-l-H+f8JCbt5HVWTjpGjjmiR4F0AznqeExfxQDcLMGuPYXgkzDmmjNQEUIxqrpxlWi2i4aVqgrgNRupytDW5VHqN1gmoB0mHM6A0BO3Q0rWgUPLmSbM7R3AmE+l+RJLj3GeO8XKH5ibAV6JDRoEkHJnoTxoTfHLvIbRbW41tKLun-I1LSRrTJCXAkwW6C6alnaqzPX8NliGUdq7mGkbSPQizakrMaRuqT1X64COaBUZm-dENwxa7NvN34yNLcSdjLBOACCximKgKAFBN0StONKwTrJ5VWhchSFoakVBlic38-yV3XE3djPUjExBxArEq4l6rDnBkcmXHhHweYJE3TIa+XzVIboCO685pxIP2xg8SFAVIRUSpEFA2APAgxH2vBLOa3MlrrXuDbWoLaH4KE9vMLjy7chru3cOp-MA39gy3bgJuxozxyS9C0GYf2VJ0cEbZFPWzVrdCfn54L2MwZRfSAgPsj2G2qtIrMCOOX0vFegpvmikw6XxwSvNtr0Ht3SfU9p5gEzGOaW+DpStSL5j6iNBvK4e4ZxlUfOCEAA */
    ...moveInMachineConfig
  });
